import React from "react";

import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps
} from "@tvg/design-system";
import { RaceVideoFeedback } from "@tvg/ts-types/Race";
import { NullaryFn } from "@tvg/ts-types/Functional";
import { ButtonVariant } from "@tvg/design-system/src/components/button/types";
import { IconButtonVariant } from "@tvg/design-system/src/components/iconButton/types";
import { getLiveStreamButtonLabels } from "../SubHeaderWrapper/utils";

interface LiveStreamLabels {
  text: string;
  qaLabel: string;
}

interface IProps {
  onClickWatchVideo: NullaryFn<void>;
  videoFeedback?: RaceVideoFeedback;
  isIconOnly?: boolean;
  withVideo?: boolean;
}

const VideoButton = ({
  onClickWatchVideo,
  videoFeedback,
  isIconOnly,
  withVideo
}: IProps) => {
  const startingSoon = [
    RaceVideoFeedback.STARTING_SOON_REPLAY,
    RaceVideoFeedback.STARTING_SOON_NO_REPLAY
  ].includes(videoFeedback as RaceVideoFeedback);

  const watchReplay = [
    RaceVideoFeedback.AVAILABLE_REPLAY,
    RaceVideoFeedback.NOT_AVAILABLE_REPLAY,
    RaceVideoFeedback.FINISHED_REPLAY,
    RaceVideoFeedback.STARTING_SOON_REPLAY
  ].includes(videoFeedback as RaceVideoFeedback);

  const liveStreaming = [
    RaceVideoFeedback.AVAILABLE_REPLAY,
    RaceVideoFeedback.AVAILABLE_NO_REPLAY
  ].includes(videoFeedback as RaceVideoFeedback);

  const notAvailable = [
    RaceVideoFeedback.NOT_AVAILABLE_NO_REPLAY,
    RaceVideoFeedback.FINISHED_NO_REPLAY
  ].includes(videoFeedback as RaceVideoFeedback);

  const playedOnNBC = [RaceVideoFeedback.SHOWN_ON_NBC].includes(
    videoFeedback as RaceVideoFeedback
  );

  const liveStreamButtonLabels = getLiveStreamButtonLabels(
    startingSoon,
    watchReplay,
    liveStreaming,
    notAvailable,
    playedOnNBC
  );

  const computeButtonProps = (
    onClickVideo: NullaryFn<void>,
    liveStreamLabel: LiveStreamLabels
  ): ButtonProps => {
    const props: Partial<ButtonProps> = {
      variant: "primary" as ButtonVariant,
      icon: "video",
      isDisabled: false,
      ml: "space-3",
      onPress: onClickVideo,
      qaLabel: liveStreamLabel.qaLabel
    };

    if (startingSoon || notAvailable || playedOnNBC) {
      props.isDisabled = true;
      props.variant = "primary";
      // the second video should be video but cut
      props.icon = startingSoon ? "video" : "noVideo";
      return props as ButtonProps;
    }

    if (watchReplay) {
      props.variant = "secondary_dark" as ButtonVariant;
      props.icon = "replay";
      return props as ButtonProps;
    }

    return props as ButtonProps;
  };

  const computeIconProps = (
    onClickVideo: NullaryFn<void>,
    liveStreamLabel: LiveStreamLabels
  ): IconButtonProps => {
    const props: Partial<IconButtonProps> = {
      iconName: "video",
      size: "s",
      variant: "primary",
      isDisabled: false,
      ml: "space-3",
      pl: "space-2",
      pr: "space-2",
      onPress: onClickVideo,
      qaLabel: liveStreamLabel.qaLabel
    };

    if (startingSoon || notAvailable || playedOnNBC) {
      props.isDisabled = true;
      props.variant = "primary";
      // the second video should be video but cut
      props.iconName = startingSoon ? "video" : "noVideo";
      return props as IconButtonProps;
    }

    if (watchReplay) {
      props.variant = "secondary_dark" as IconButtonVariant;
      props.iconName = "replay";
      return props as IconButtonProps;
    }

    return props as IconButtonProps;
  };

  return isIconOnly ? (
    <IconButton
      {...computeIconProps(onClickWatchVideo, liveStreamButtonLabels)}
      isActive={withVideo}
    />
  ) : (
    <Button {...computeButtonProps(onClickWatchVideo, liveStreamButtonLabels)}>
      {liveStreamButtonLabels.text}
    </Button>
  );
};

export default VideoButton;
